import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAlert } from 'react-alert';
import MetaData from '../../layouts/MetaData';
import RoleShell from '../../components/Enrollment/RoleShell';
import DeviceRequestForm from '../../components/DeviceRequests/DeviceRequestForm';
import DeviceRequestTimeline from '../../components/DeviceRequests/DeviceRequestTimeline';
import { DR_API, STATUS, NURSE_ROLES, REVIEW_ROLES, errMsg, fmtDateTime, patientLabel, typeLabel } from '../../components/DeviceRequests/deviceRequestApi';
import { isObjectId } from '../../utils/apiError';

const NURSE_EDIT = ['SUBMITTED', 'RETURNED'];
const REVIEW_EDIT = ['SUBMITTED', 'RETURNED', 'APPROVED', 'CANNOT_FULFIL'];

/** Request detail: nurse fixes/resubmits, Org Manager edits/approves/returns, everyone with access sees the timeline. */
const DeviceRequestDetail = () => {
    const { id } = useParams();
    const history = useHistory();
    const alert = useAlert();
    const { user } = useSelector((s) => s.auth);
    const role = user?.role;
    const [r, setR] = useState(null);
    const [error, setError] = useState('');
    const [reasonFor, setReasonFor] = useState('');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [formKey, setFormKey] = useState(0);

    const load = useCallback(async () => {
        if (!isObjectId(id)) { setError('This device request could not be found.'); return; }
        try {
            const { data } = await axios.get(`${DR_API}/${id}`);
            setR(data);
            setFormKey((k) => k + 1);
        } catch (err) { setError(errMsg(err)); }
    }, [id]);
    useEffect(() => { load(); }, [load]);

    const act = async (fn, ok) => {
        setBusy(true);
        setError('');
        try { await fn(); alert.success(ok); setReasonFor(''); setReason(''); await load(); }
        catch (err) { setError(errMsg(err)); }
        finally { setBusy(false); }
    };
    if (!r) return <RoleShell title="Device Request">{error ? <div className="alert alert-danger">{error}</div> : 'Loading…'}</RoleShell>;

    const isReviewer = REVIEW_ROLES.includes(role);
    const canEdit = (isReviewer && REVIEW_EDIT.includes(r.status)) || (NURSE_ROLES.includes(role) && NURSE_EDIT.includes(r.status));
    const resubmitMode = r.status === 'RETURNED' && (NURSE_ROLES.includes(role) || isReviewer);
    const save = async (payload, ack) => {
        if (resubmitMode) {
            await axios.post(`${DR_API}/${id}/resubmit`, { ...payload, ...(ack ? { acknowledgeDuplicates: true } : {}) });
            alert.success('Resubmitted to the Org Manager');
        } else {
            await axios.patch(`${DR_API}/${id}`, payload);
            alert.success('Saved');
        }
        await load();
    };
    const st = STATUS[r.status] || {};

    return (
        <RoleShell title="Device Request">
            <MetaData title={`Device Request ${r.requestNo}`} />
            <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                    <h5 className="mb-1">RPM Device Request {r.requestNo} <span className={`badge badge-${st.bg}`}>{st.label || r.status}</span></h5>
                    <div className="small text-muted">
                        {patientLabel(r.patient)} · {r.organization?.name} · {(r.deviceTypes || []).map(typeLabel).join(', ')} · submitted by {r.createdByName} {fmtDateTime(r.submittedAt)}
                        {r.batch && <> · batch <Link to={`/device-requests/batches/${r.batch._id}`}>{r.batch.batchNo}</Link></>}
                    </div>
                </div>
                <Link to="/device-requests" className="btn btn-link btn-sm">Back to list</Link>
            </div>
            {r.status === 'RETURNED' && r.returnReason && <div className="alert alert-warning py-2"><strong>Returned:</strong> {r.returnReason}</div>}
            {r.status === 'CANNOT_FULFIL' && r.cannotFulfilReason && <div className="alert alert-danger py-2"><strong>EC cannot fulfil:</strong> {r.cannotFulfilReason}</div>}
            {(r.duplicateWarnings || []).length > 0 && (
                <div className="alert alert-warning py-2">{r.duplicateWarnings.map((w) => <div key={w.message}>{w.message}</div>)}</div>
            )}
            {isReviewer && ['SUBMITTED', 'CANNOT_FULFIL'].includes(r.status) && (r.missingForApproval || []).length > 0 && (
                <div className="alert alert-info py-2" data-testid="dr-missing">To approve, complete: {r.missingForApproval.join('; ')}</div>
            )}
            {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}

            <div className="row">
                <div className="col-lg-8">
                    <DeviceRequestForm key={formKey} mode={resubmitMode ? 'resubmit' : 'edit'} request={r} disabled={!canEdit || busy}
                        submitLabel={resubmitMode ? 'Fix & resubmit' : 'Save changes'} onSubmit={save} />
                    {(r.items || []).some((i) => i.status === 'FULFILLED') && (
                        <div className="mt-3"><strong>Assigned devices</strong>
                            <ul>{r.items.filter((i) => i.status === 'FULFILLED').map((i) => <li key={i._id}>{typeLabel(i.deviceType)}: IMEI {i.imei} (device {i.deviceId}) by {i.fulfilledByName} {fmtDateTime(i.fulfilledAt)}</li>)}</ul>
                        </div>
                    )}
                </div>
                <div className="col-lg-4">
                    <div className="card mb-3"><div className="card-body">
                        <h6>Actions</h6>
                        {isReviewer && ['SUBMITTED', 'CANNOT_FULFIL'].includes(r.status) && (
                            <button type="button" className="btn btn-success btn-sm mr-2 mb-2" disabled={busy} onClick={() => act(() => axios.post(`${DR_API}/${id}/approve`), "Approved into today's batch")}>
                                Approve into today&apos;s batch
                            </button>
                        )}
                        {isReviewer && ['SUBMITTED', 'APPROVED', 'CANNOT_FULFIL'].includes(r.status) && (
                            <button type="button" className="btn btn-warning btn-sm mr-2 mb-2" disabled={busy} onClick={() => setReasonFor('return')}>Return to nurse</button>
                        )}
                        {((isReviewer && REVIEW_EDIT.includes(r.status)) || (NURSE_ROLES.includes(role) && NURSE_EDIT.includes(r.status))) && (
                            <button type="button" className="btn btn-outline-danger btn-sm mb-2" disabled={busy} onClick={() => setReasonFor('cancel')}>Cancel request</button>
                        )}
                        {r.status === 'APPROVED' && isReviewer && <div className="small"><Link to="/device-requests/daily">Send with today&apos;s Daily Enrollment Request →</Link></div>}
                        {reasonFor && (
                            <div className="mt-2">
                                <label className="form-label small" htmlFor="dr-reason">{reasonFor === 'return' ? 'Reason for returning to the nurse' : 'Reason for cancelling'} *</label>
                                <textarea id="dr-reason" className="form-control" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                                <button type="button" className="btn btn-primary btn-sm mt-1" disabled={busy || reason.trim().length < 3}
                                    onClick={() => act(() => axios.post(`${DR_API}/${id}/${reasonFor}`, { reason }), reasonFor === 'return' ? 'Returned to nurse' : 'Cancelled')}>Confirm</button>
                                <button type="button" className="btn btn-link btn-sm mt-1" onClick={() => setReasonFor('')}>Close</button>
                            </div>
                        )}
                        {role === 'admin' && ['SENT_TO_EC', 'IN_PROGRESS', 'PARTIAL'].includes(r.status) && (
                            <div className="small"><Link to={`/enrollment/device-requests/${id}`}>Open fulfilment screen →</Link></div>
                        )}
                    </div></div>
                    <div className="card"><div className="card-body"><h6>Timeline</h6><DeviceRequestTimeline history={r.history} /></div></div>
                </div>
            </div>
            <button type="button" className="btn btn-link btn-sm" onClick={() => history.goBack()}>← Back</button>
        </RoleShell>
    );
};

export default DeviceRequestDetail;
